import {
  PhoneInput,
  UtilityTextInputBase,
  inputValueToPhoneString,
  phoneStringToInputValue,
  Switch,
} from "@jugl-web/ui-kit";
import { useTranslations, cx } from "@jugl-web/utils";
import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { useVoiceAccount } from "../../../../hooks/useVoiceAccount";
import { VoiceTransferFormValues } from "../../types";

export const TransferCallSection: React.FC = () => {
  const { t } = useTranslations();
  const { account } = useVoiceAccount();
  const {
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext<VoiceTransferFormValues>();

  const isTransferEnabled = useWatch({
    control,
    name: "transfer_enabled",
  });

  const juglPhoneNumber = account?.phone_num ?? "";

  return (
    <CRMInfoSection
      title={
        <span className="flex items-center gap-2.5">
          <Controller
            control={control}
            name="transfer_enabled"
            render={({ field: { value, onChange } }) => (
              <Switch
                isChecked={value}
                onChange={(checked) => {
                  onChange(checked);
                  if (!checked) {
                    clearErrors("transfer_number");
                  }
                }}
              />
            )}
          />
          {t({
            id: "client-support-page.transfer-call-to-human-agent",
            defaultMessage: "Transfer call to Human Agent",
          })}
        </span>
      }
      subtitle={t({
        id: "client-support-page.transfer-call-to-human-agent-description",
        defaultMessage:
          "Indicate your private business number for calls handover",
      })}
    >
      <Controller
        control={control}
        name="transfer_number"
        rules={{
          validate: (value, formValues) => {
            if (!formValues.transfer_enabled) {
              return true;
            }

            const phoneInput = phoneStringToInputValue(value?.trim() ?? "");

            if (!phoneInput.isValid) {
              return t({
                id: "feedback.invalid-phone-number",
                defaultMessage: "Invalid phone number",
              });
            }

            const phone = inputValueToPhoneString(phoneInput);

            if (juglPhoneNumber && phone === juglPhoneNumber) {
              return t({
                id: "client-support-page.jugl-phone-number-cant-be-used-for-transferring",
                defaultMessage:
                  "Your Jugl Phone Number can't be used for transferring. Indicate another trusted number",
              });
            }

            return true;
          },
        }}
        render={({ field: { value, onChange } }) => (
          <UtilityTextInputBase
            label={t({
              id: "client-support-page.transfer-number",
              defaultMessage: "Transfer Number",
            })}
            isRequired={isTransferEnabled}
            isInvalid={!!errors.transfer_number}
            errorMessage={errors.transfer_number?.message}
            isDisabled={!isTransferEnabled}
            inputWrapperClassName={cx(
              !isTransferEnabled && "pointer-events-none"
            )}
          >
            <PhoneInput
              value={phoneStringToInputValue(value || "")}
              wrapperClassName="h-15 w-full border-none"
              hideVerticalLine
              onChange={(nextValue) =>
                onChange(inputValueToPhoneString(nextValue))
              }
            />
          </UtilityTextInputBase>
        )}
      />
    </CRMInfoSection>
  );
};
